// Global skill attribution shared by the native cold compiler and held status delta.
// Integer rollup owns the post-mutation totals and source rows; this module only
// patches affected skill entries. Forced-roll siblings below retain their own contract.
import { phpJsonInt } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const intOf = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isInteger(v)) return v;
    if (typeof v === 'string' && /^\s*[+-]?\d+\s*$/.test(v)) return parseInt(v.trim(), 10);
    return null;
};
/** Σ of a layer's integer fragments (a scalar, a numeric string, or a list of them); null when any fragment is not a plain integer. */
const layerInt = (v: unknown): number | null => {
    if (v === undefined || v === null || v === '') return 0;
    const list = Array.isArray(v) ? v : [v];
    let sum = 0;
    for (const f of list) { const n = intOf(f); if (n === null) return null; sum += n; }
    return sum;
};

export interface StatusSkillGlobalsInput {
    skills: Row | undefined;
    modifiers: Row | undefined;
    modifierSources: Row | undefined;
    /** the status definition's OWN item_layer_types */
    layers: Row;
    statusName: string;
    statusRowId: string;
    applied: boolean;
    /** Skill::ALL — variable_name + item_type (defensive / innate skills default to tier 1 and are never elided) */
    skillEntries: ReadonlyArray<{ variable_name: string; item_type: string }>;
    /** target status row ids held AFTER the mutation, for source attribution and legacy zero-net retention */
    heldStatusIds: ReadonlySet<string>;
    /** 2026-09-28 (T4e+ / the Maimed stack fix): how many STACKS of the status move — a quantity family's skill_rolls_mod residue is
     *  one stack's value × its stack (layer_on_status_effects ft_status_row_scale_residue_fragment). Default 1. */
    stacks?: number;
    /** Existing source walker; consulted only for an unattributed remainder. */
    sourceNames?: (layer: string) => readonly string[];
    /** Existing status/name reader, for lean source rows that omit their label. */
    sourceLabel?: (statusId: string) => string | undefined;
}
export type StatusSkillGlobalsResult = { ok: true; skills: Row; modifiers: Row | undefined; modifierSources: Row | undefined; touched: boolean } | { ok: false; reason: string };

export const STATUS_SKILL_GLOBAL_LAYERS = ['skill_rolls_adv', 'skill_rolls_dis', 'skill_rolls_mod'] as const;
const ENTRY_ORDER = ['tier_level', 'sources', 'adv_dis_stacks', 'adv_dis_breakdown', 'raw_sp', 'forced_natural_main_roll', 'skill_rolls_mod', 'skill_roll_mod', 'skill_roll_mod_breakdown'];

/** Shared held-skill list reader, including raw names and wrapped references. */
export function layerEntryTargetsSkill(entry: unknown, skillId: string, variableName: string): boolean {
    const test = (value: unknown): boolean => {
        if (typeof value === 'string') {
            const token = value.trim().split('|', 1)[0] ?? '';
            return upper(token) === upper(skillId) || token.toLowerCase() === variableName.toLowerCase();
        }
        if (!isObj(value)) return false;
        return test(value.item_layer_effect ?? value.item_id ?? (isObj(value.items) ? value.items.item_id : null) ?? value.variable_name ?? value[0]);
    };
    return test(entry);
}

/** Bounded status attribution followed by the ordered, signed non-status remainder.
 * Both cold compilation and held deltas use this rule; no PHP or UI evaluator. */
export function statusSkillGlobalLines(layer: string, total: number, sourceRows: unknown, names?: () => readonly string[],
    identity?: { ids: ReadonlySet<string>; label?: (statusId: string) => string | undefined }): Row[] {
    if (total === 0) return [];
    const sources = sourceRows !== null && typeof sourceRows === 'object' ? Object.values(sourceRows) : [];
    let lines: Row[] = [], attributed = 0, statusNames: string[] = [];
    for (const source of sources) {
        if (!isObj(source)) continue;
        const id = source.href_id, amount = source.value;
        const category = source.category ?? source.kind;
        // Store-point lean can omit both category and kind. A held target row
        // proves the same source identity without inventing one for a remainder.
        if (category !== 'status_effect' && !(category == null && identity?.ids.has(upper(id)))) continue;
        if (typeof id !== 'string' || !/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id)
            || !isPhpNumeric(amount) || phpJsonInt(amount) <= 0) continue;
        const value = phpJsonInt(amount) * (layer === 'skill_rolls_dis' ? -1 : 1);
        const label = typeof source.label === 'string' ? source.label : identity?.label?.(upper(id));
        attributed += value; if (label) statusNames.push(label);
        lines.push({ value, ...(label === undefined ? {} : { label }), kind: 'adv_dis', href_id: upper(id), global_layer: layer });
    }
    if (Math.abs(attributed) > Math.abs(total) || (attributed !== 0 && (attributed > 0) !== (total > 0))) {
        lines = []; attributed = 0; statusNames = [];
    }
    const remaining = total - attributed;
    if (remaining !== 0) {
        const labels = [...new Set((names ? names() : sources.filter(isObj).map(source => String(source.label ?? '')))
            .filter(name => name !== '' && !statusNames.includes(name)))];
        const magnitude = Math.abs(remaining);
        lines.push({ value: remaining,
            label: `Global — ${remaining > 0 ? '+' : '−'}${magnitude} stack${magnitude === 1 ? '' : 's'} from ${layer}${labels.length ? ' · ' + labels.join(', ') : ''}`,
            kind: 'adv_dis', href_id: null, global_layer: layer });
    }
    return lines;
}

const linesOf = (entry: Row): Row[] => isObj(entry.adv_dis_breakdown) && Array.isArray(entry.adv_dis_breakdown.lines)
    ? entry.adv_dis_breakdown.lines.filter(isObj) : [];
const sameRow = (a: Row, b: Row): boolean => Object.keys(a).length === Object.keys(b).length
    && Object.keys(a).every(key => Object.is(a[key], b[key]));
const sameLines = (a: Row[], b: Row[]): boolean => a.length === b.length && a.every((line, i) => sameRow(line, b[i]!));

export function applyStatusSkillGlobals(input: StatusSkillGlobalsInput): StatusSkillGlobalsResult {
    const priorSkills = input.skills ?? {};
    const globalTouched = (['skill_rolls_adv', 'skill_rolls_dis'] as const).some(layer => {
        const value = layerInt(input.layers[layer]); return value === null || value !== 0;
    });
    const modOne = layerInt(input.layers.skill_rolls_mod);
    if (modOne === null) return { ok: false, reason: 'status-skills-global:formula:skill_rolls_mod' };
    const stacks = Number.isInteger(input.stacks) && (input.stacks as number) > 0 ? input.stacks as number : 1;
    const modDelta = modOne * stacks * (input.applied ? 1 : -1);
    const globalLines: Row[] = [];
    if (globalTouched) for (const layer of ['skill_rolls_adv', 'skill_rolls_dis'] as const) {
        if (layerInt(input.layers[layer]) === null) return { ok: false, reason: `status-skills-global:formula:${layer}` };
        const total = intOf(input.modifiers?.[layer] ?? 0);
        if (total === null) return { ok: false, reason: `status-skills-global:total-shape:${layer}` };
        globalLines.push(...statusSkillGlobalLines(layer, total * (layer === 'skill_rolls_dis' ? -1 : 1),
            input.modifierSources?.[layer], input.sourceNames ? () => input.sourceNames!(layer) : undefined,
            { ids: input.heldStatusIds, label: input.sourceLabel }));
    }
    let nextMod = 0;
    if (modDelta !== 0) {
        const stamped = new Set<number>();
        for (const { variable_name: variable } of input.skillEntries) {
            const entry = priorSkills[variable];
            const value = intOf(isObj(entry) ? entry.skill_rolls_mod ?? 0 : 0);
            if (value === null) return { ok: false, reason: 'status-skills-global:mod-shape' };
            stamped.add(value);
        }
        if (stamped.size > 1) return { ok: false, reason: 'status-skills-global:mod-inconsistent' };
        nextMod = ([...stamped][0] ?? 0) + modDelta;
    }
    // Plan every changed entry before publishing a map. A late refusal has no
    // partial held writes and needs no rollback snapshot.
    const changes = new Map<string, Row | undefined>();
    if (globalTouched || modDelta !== 0) for (const { variable_name: variable, item_type: itemType } of input.skillEntries) {
        const prior = isObj(priorSkills[variable]) ? priorSkills[variable] as Row : undefined;
        if (!prior && !globalLines.length && nextMod === 0) continue;
        if (!prior && (itemType === 'defensive_skill' || itemType === 'innate_skill'))
            return { ok: false, reason: `status-skills-global:elided-defensive:${variable}` };
        const entry: Row = prior ? { ...prior } : { tier_level: 0, sources: ['default_flawed'] };
        if (globalTouched) {
            const before = linesOf(entry);
            const after = [...globalLines, ...before.filter(line => !line.global_layer)];
            const lines = sameLines(before, after) ? before : after.map((line, i) =>
                before[i] && sameRow(line, before[i]!) ? before[i]! : line);
            const net = lines.reduce((sum, line) => sum + (intOf(line.value) ?? 0), 0);
            const reversible = lines.some(line => typeof line.href_id === 'string' && input.heldStatusIds.has(upper(line.href_id)));
            if (net !== 0) entry.adv_dis_stacks = net; else delete entry.adv_dis_stacks;
            if (net !== 0 || (lines.length > 0 && (reversible || isObj(prior?.adv_dis_breakdown)))) {
                if (lines !== before || !isObj(entry.adv_dis_breakdown)) entry.adv_dis_breakdown = { lines };
            } else delete entry.adv_dis_breakdown;
        }
        if (modDelta !== 0) { if (nextMod !== 0) entry.skill_rolls_mod = nextMod; else delete entry.skill_rolls_mod; }
        const pureDefault = entry.tier_level === 0 && Array.isArray(entry.sources) && entry.sources.length === 1
            && entry.sources[0] === 'default_flawed' && Object.keys(entry).every(key => key === 'tier_level' || key === 'sources');
        if (pureDefault) { if (prior) changes.set(variable, undefined); continue; }
        if (prior && sameRow(prior, entry)) continue;
        const ordered: Row = {};
        for (const key of ENTRY_ORDER) if (key in entry) ordered[key] = entry[key];
        for (const key of Object.keys(entry)) if (!(key in ordered)) ordered[key] = entry[key];
        changes.set(variable, ordered);
    }
    let skills = priorSkills;
    if (changes.size) {
        skills = { ...priorSkills };
        for (const [variable, entry] of changes) { if (entry) skills[variable] = entry; else delete skills[variable]; }
    }
    return { ok: true, skills, modifiers: input.modifiers, modifierSources: input.modifierSources, touched: changes.size !== 0 };
}

export interface StatusForcedRollInput {
    skills: Row | undefined;
    /** skill ids the MUTATED status lists, per layer */
    statusLists: { min: string[]; max: string[] };
    /** every definition's lists that still force a roll AFTER the mutation: the host ROOT lists + each held target status's */
    remaining: { min: Set<string>; max: Set<string> };
    skillEntries: ReadonlyArray<{ item_id: string; variable_name: string; item_type: string }>;
}
/**
 * 2026-09-24 late: a status's `skill_is_rolled_at_min` / `_at_max` — resolve_caster_skill_forced_natural_main_roll: a skill listed by
 * the host ROOT or by any held target status's status_arr is forced to a natural 20 (max wins) or 1; build_caster_skills stores it
 * as `forced_natural_main_roll` (a non-null value keeps / mints the entry; its absence lets the pure-default elide gate drop it).
 * Only the skills the mutated status lists can move; each is recomputed from what still forces it after the mutation.
 */
export function applyStatusForcedRoll(input: StatusForcedRollInput): StatusSkillGlobalsResult {
    const skills: Row = isObj(input.skills) ? JSON.parse(JSON.stringify(input.skills)) as Row : {};
    const listed = new Set([...input.statusLists.min, ...input.statusLists.max].map(upper));
    if (!listed.size) return { ok: true, skills, modifiers: undefined, modifierSources: undefined, touched: false };
    for (const skill of input.skillEntries) {
        const id = upper(skill.item_id);
        if (!listed.has(id)) continue;
        const forced = input.remaining.max.has(id) ? 20 : input.remaining.min.has(id) ? 1 : null;
        const had = isObj(skills[skill.variable_name]);
        if (!had && forced === null) continue;
        if (!had) {
            if (skill.item_type === 'defensive_skill' || skill.item_type === 'innate_skill') return { ok: false, reason: `status-forced-roll:elided-defensive:${skill.variable_name}` };
            skills[skill.variable_name] = { tier_level: 0, sources: ['default_flawed'] };
        }
        const e = skills[skill.variable_name] as Row;
        if (forced === null) delete e.forced_natural_main_roll; else e.forced_natural_main_roll = forced;
        const pureDefault = e.tier_level === 0 && Array.isArray(e.sources) && e.sources.length === 1 && e.sources[0] === 'default_flawed'
            && Object.keys(e).every(k => k === 'tier_level' || k === 'sources');
        if (pureDefault) { delete skills[skill.variable_name]; continue; }
        const ordered: Row = {};
        for (const k of ENTRY_ORDER) if (k in e) ordered[k] = e[k];
        for (const k of Object.keys(e)) if (!(k in ordered)) ordered[k] = e[k];
        skills[skill.variable_name] = ordered;
    }
    return { ok: true, skills, modifiers: undefined, modifierSources: undefined, touched: true };
}
